import React, { useState, useEffect, useRef, Component } from 'react';
import { ChevronLeft, ChevronRight, Check, Type, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Canvas, IText, FabricImage, Circle, Rect } from 'fabric';
import * as fabric from 'fabric';
import QRCode from 'qrcode';

// Simple Error Boundary for canvas
class CanvasErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.log('Canvas error caught:', error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <div className="p-4 text-center text-gray-500">Canvas loading...</div>;
    }
    return this.props.children;
  }
}

const CustomDesignerWizard = ({ product, onAddToCart }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [customization, setCustomization] = useState({
    design: null,
    canvasData: null,
    from: '',
    to: '',
    background: product?.images?.[0] || null
  });
  const [canvasKey, setCanvasKey] = useState(0); // Key to force re-render canvas
  
  // Collapsible sections state
  const [openSection, setOpenSection] = useState('text'); // 'text', 'photo', 'clipart', 'ar' or null

  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [textColor, setTextColor] = useState('#000000');
  const [fontSize, setFontSize] = useState(32);
  const [fontFamily, setFontFamily] = useState('Arial');
  const [liveTextObject, setLiveTextObject] = useState(null);
  const fileInputRef = useRef(null);
  const [selectedShape, setSelectedShape] = useState('rectangle');
  const [selectedFrame, setSelectedFrame] = useState('none');

  const steps = [
    {
      id: 'design',
      title: 'Select Card',
      description: 'Choose your card background'
    },
    {
      id: 'editor',
      title: 'Design Your Card',
      description: 'Add text, photos & AR video'
    },
    {
      id: 'addresses',
      title: 'Envelope Address',
      description: 'From & To for mailing'
    },
    {
      id: 'preview',
      title: 'Review Order',
      description: 'Final check'
    }
  ];

  // AR Options
  const arAnimations = [
    { id: 'hearts', name: 'Hearts', icon: '💖', description: 'Floating hearts animation' },
    { id: 'stars', name: 'Stars', icon: '⭐', description: 'Sparkling stars effect' },
    { id: 'celebration', name: 'Celebration', icon: '🎉', description: 'Party confetti & balloons' },
    { id: 'nature', name: 'Nature', icon: '🌸', description: 'Flowers & butterflies' },
    { id: 'love', name: 'Love', icon: '💕', description: 'Romantic hearts & roses' }
  ];

  const [arEnabled, setArEnabled] = useState(false);
  const [arAnimation, setArAnimation] = useState('hearts');
  const [arMessage, setArMessage] = useState('You have a special greeting!');
  const [arRevealMessage, setArRevealMessage] = useState('🎉 Surprise! You are amazing! 🎉');
  const [arVideo, setArVideo] = useState(null);
  const [arVideoUrl, setArVideoUrl] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [arQrCode, setArQrCode] = useState(null);
  const [arGreetingId, setArGreetingId] = useState(null);
  const [pendingQrAdd, setPendingQrAdd] = useState(false); // Flag to add QR when canvas is ready
  const [qrAddedToCanvas, setQrAddedToCanvas] = useState(false); // Track if QR was already added
  const videoInputRef = useRef(null);

  const designs = product?.images || [];
  const colors = ['#000000', '#FFFFFF', '#FF0000', '#0000FF', '#FF69B4', '#FFD700', '#8B4513', '#00FF00'];
  const fonts = [
    // Sans-Serif Fonts
    'Arial',
    'Helvetica',
    'Verdana',
    'Tahoma',
    'Trebuchet MS',
    'Gill Sans',
    'Century Gothic',
    'Futura',
    'Franklin Gothic',
    'Calibri',
    // Serif Fonts
    'Georgia', 
    'Times New Roman',
    'Palatino',
    'Garamond',
    'Bookman',
    'Didot',
    'Bodoni',
    'Cambria',
    'Baskerville',
    'Rockwell',
    // Script & Decorative Fonts
    'Brush Script MT',
    'Lucida Handwriting',
    'Segoe Script',
    'Comic Sans MS',
    'Papyrus',
    'Copperplate',
    'American Typewriter',
    'Chalkduster',
    'Marker Felt',
    'Bradley Hand',
    // Monospace Fonts
    'Courier New',
    'Lucida Console',
    'Monaco',
    'Consolas',
    // Display Fonts
    'Impact',
    'Optima',
    'Luminari',
    'Snell Roundhand',
    'Zapfino'
  ];
  const shapes = [
    { id: 'rectangle', name: 'Rectangle', icon: '▭' },
    { id: 'square', name: 'Square', icon: '◻' },
    { id: 'circle', name: 'Circle', icon: '○' },
    { id: 'rounded', name: 'Rounded', icon: '▢' }
  ];
  const frames = [
    { id: 'none', name: 'No Frame' },
    { id: 'simple', name: 'Simple Border' },
    { id: 'double', name: 'Double Border' },
    { id: 'shadow', name: 'Shadow' }
  ];

  // Free Clipart categories with PNG/SVG images
  const clipartCategories = [
    { id: 'birthday', name: '🎂 Birthday', icon: '🎂' },
    { id: 'love', name: '❤️ Love', icon: '❤️' },
    { id: 'celebration', name: '🎉 Celebration', icon: '🎉' },
    { id: 'nature', name: '🌸 Nature', icon: '🌸' },
    { id: 'decorative', name: '✨ Decorative', icon: '✨' },
    { id: 'frames', name: '🖼️ Frames', icon: '🖼️' }
  ];

  // Free PNG/SVG clipart URLs (using free clipart sources)
  const clipartItems = {
    birthday: [
      { id: 'balloon-red', name: 'Red Balloon', url: 'https://cdn-icons-png.flaticon.com/512/3656/3656855.png' },
      { id: 'balloon-bunch', name: 'Balloon Bunch', url: 'https://cdn-icons-png.flaticon.com/512/3656/3656826.png' },
      { id: 'cake', name: 'Birthday Cake', url: 'https://cdn-icons-png.flaticon.com/512/3656/3656951.png' },
      { id: 'gift', name: 'Gift Box', url: 'https://cdn-icons-png.flaticon.com/512/3656/3656869.png' },
      { id: 'party-hat', name: 'Party Hat', url: 'https://cdn-icons-png.flaticon.com/512/3656/3656894.png' },
      { id: 'confetti', name: 'Confetti', url: 'https://cdn-icons-png.flaticon.com/512/3656/3656836.png' },
      { id: 'cupcake', name: 'Cupcake', url: 'https://cdn-icons-png.flaticon.com/512/3656/3656970.png' },
      { id: 'candles', name: 'Candles', url: 'https://cdn-icons-png.flaticon.com/512/3656/3656963.png' }
    ],
    love: [
      { id: 'heart-red', name: 'Red Heart', url: 'https://cdn-icons-png.flaticon.com/512/833/833472.png' },
      { id: 'heart-pink', name: 'Pink Heart', url: 'https://cdn-icons-png.flaticon.com/512/833/833300.png' },
      { id: 'hearts-multi', name: 'Hearts', url: 'https://cdn-icons-png.flaticon.com/512/833/833605.png' },
      { id: 'rose', name: 'Rose', url: 'https://cdn-icons-png.flaticon.com/512/2477/2477132.png' },
      { id: 'roses-bouquet', name: 'Bouquet', url: 'https://cdn-icons-png.flaticon.com/512/2477/2477053.png' },
      { id: 'love-letter', name: 'Love Letter', url: 'https://cdn-icons-png.flaticon.com/512/833/833516.png' },
      { id: 'cupid', name: 'Cupid Arrow', url: 'https://cdn-icons-png.flaticon.com/512/833/833468.png' },
      { id: 'ring', name: 'Ring', url: 'https://cdn-icons-png.flaticon.com/512/833/833621.png' }
    ],
    celebration: [
      { id: 'trophy', name: 'Trophy', url: 'https://cdn-icons-png.flaticon.com/512/3112/3112946.png' },
      { id: 'medal-gold', name: 'Gold Medal', url: 'https://cdn-icons-png.flaticon.com/512/3112/3112829.png' },
      { id: 'champagne', name: 'Champagne', url: 'https://cdn-icons-png.flaticon.com/512/3656/3656841.png' },
      { id: 'fireworks', name: 'Fireworks', url: 'https://cdn-icons-png.flaticon.com/512/3656/3656879.png' },
      { id: 'star-gold', name: 'Gold Star', url: 'https://cdn-icons-png.flaticon.com/512/1828/1828884.png' },
      { id: 'ribbon', name: 'Ribbon', url: 'https://cdn-icons-png.flaticon.com/512/3112/3112862.png' },
      { id: 'crown', name: 'Crown', url: 'https://cdn-icons-png.flaticon.com/512/3112/3112892.png' },
      { id: 'diploma', name: 'Diploma', url: 'https://cdn-icons-png.flaticon.com/512/3112/3112800.png' }
    ],
    nature: [
      { id: 'flower-pink', name: 'Pink Flower', url: 'https://cdn-icons-png.flaticon.com/512/2477/2477107.png' },
      { id: 'sunflower', name: 'Sunflower', url: 'https://cdn-icons-png.flaticon.com/512/2477/2477157.png' },
      { id: 'tulip', name: 'Tulip', url: 'https://cdn-icons-png.flaticon.com/512/2477/2477175.png' },
      { id: 'butterfly', name: 'Butterfly', url: 'https://cdn-icons-png.flaticon.com/512/616/616412.png' },
      { id: 'rainbow', name: 'Rainbow', url: 'https://cdn-icons-png.flaticon.com/512/3468/3468293.png' },
      { id: 'sun', name: 'Sun', url: 'https://cdn-icons-png.flaticon.com/512/3222/3222791.png' },
      { id: 'cloud', name: 'Cloud', url: 'https://cdn-icons-png.flaticon.com/512/3222/3222808.png' },
      { id: 'tree', name: 'Tree', url: 'https://cdn-icons-png.flaticon.com/512/2477/2477195.png' }
    ],
    decorative: [
      { id: 'stars', name: 'Stars', url: 'https://cdn-icons-png.flaticon.com/512/1828/1828970.png' },
      { id: 'sparkle', name: 'Sparkle', url: 'https://cdn-icons-png.flaticon.com/512/3468/3468081.png' },
      { id: 'bow', name: 'Bow', url: 'https://cdn-icons-png.flaticon.com/512/3656/3656859.png' },
      { id: 'bell', name: 'Bell', url: 'https://cdn-icons-png.flaticon.com/512/3468/3468150.png' },
      { id: 'snowflake', name: 'Snowflake', url: 'https://cdn-icons-png.flaticon.com/512/2336/2336319.png' },
      { id: 'music', name: 'Music Note', url: 'https://cdn-icons-png.flaticon.com/512/3844/3844649.png' },
      { id: 'gem', name: 'Diamond', url: 'https://cdn-icons-png.flaticon.com/512/2913/2913136.png' },
      { id: 'feather', name: 'Feather', url: 'https://cdn-icons-png.flaticon.com/512/2913/2913463.png' }
    ],
    frames: [
      { id: 'frame-gold', name: 'Gold Frame', url: 'https://cdn-icons-png.flaticon.com/512/4211/4211763.png' },
      { id: 'frame-ornate', name: 'Ornate Frame', url: 'https://cdn-icons-png.flaticon.com/512/4211/4211668.png' },
      { id: 'banner', name: 'Banner', url: 'https://cdn-icons-png.flaticon.com/512/3468/3468233.png' },
      { id: 'scroll', name: 'Scroll', url: 'https://cdn-icons-png.flaticon.com/512/3468/3468263.png' },
      { id: 'wreath', name: 'Wreath', url: 'https://cdn-icons-png.flaticon.com/512/2477/2477043.png' },
      { id: 'corner', name: 'Corner', url: 'https://cdn-icons-png.flaticon.com/512/4211/4211708.png' },
      { id: 'divider', name: 'Divider', url: 'https://cdn-icons-png.flaticon.com/512/4211/4211724.png' },
      { id: 'label', name: 'Label', url: 'https://cdn-icons-png.flaticon.com/512/3468/3468212.png' }
    ]
  };

  const [selectedClipartCategory, setSelectedClipartCategory] = useState('birthday');
  const [clipartLoading, setClipartLoading] = useState(false);

  // Initialize fabric canvas
  useEffect(() => {
    // Only initialize canvas when on step 1 and canvas element exists
    if (currentStep === 1 && canvasRef.current) {
      // If canvas was already initialized, just re-render
      if (fabricCanvasRef.current) {
        try {
          fabricCanvasRef.current.renderAll();
        } catch (e) {
          console.log('Canvas render error, reinitializing');
          fabricCanvasRef.current = null;
        }
      }
      
      // Initialize new canvas if needed
      if (!fabricCanvasRef.current) {
        try {
          const canvas = new Canvas(canvasRef.current, {
            width: 600,
            height: 400,
            backgroundColor: '#ffffff',
          });

          fabricCanvasRef.current = canvas;

          // Handle object selection
          canvas.on('selection:created', (e) => {
            setSelectedObject(e.selected[0]);
          });

          canvas.on('selection:updated', (e) => {
            setSelectedObject(e.selected[0]);
          });

          canvas.on('selection:cleared', () => {
            setSelectedObject(null);
          });

          // Load background immediately if selected
          if (customization.background) {
            FabricImage.fromURL(customization.background, { crossOrigin: 'anonymous' })
              .then((img) => {
                if (!fabricCanvasRef.current) return;
                const scaleX = canvas.width / img.width;
                const scaleY = canvas.height / img.height;
                img.scale(Math.max(scaleX, scaleY));
                img.set({
                  left: canvas.width / 2,
                  top: canvas.height / 2,
                  originX: 'center',
                  originY: 'center',
                  selectable: false,
                  evented: false
                });
                canvas.backgroundImage = img;
                canvas.renderAll();
              })
              .catch(err => console.error('Error loading background:', err));
          }
        } catch (e) {
          console.error('Canvas initialization error:', e);
        }
      }
    }
  }, [currentStep]);

  // Cleanup canvas only on component unmount
  useEffect(() => {
    return () => {
      if (fabricCanvasRef.current) {
        try {
          fabricCanvasRef.current.dispose();
        } catch (e) {
          console.log('Canvas dispose error');
        }
        fabricCanvasRef.current = null;
      }
    };
  }, []);

  // Update background when changed
  useEffect(() => {
    if (fabricCanvasRef.current && customization.background && currentStep === 1) {
      FabricImage.fromURL(customization.background, { crossOrigin: 'anonymous' })
        .then((img) => {
          const canvas = fabricCanvasRef.current;
          const scaleX = canvas.width / img.width;
          const scaleY = canvas.height / img.height;
          img.scale(Math.max(scaleX, scaleY));
          img.set({
            left: canvas.width / 2,
            top: canvas.height / 2,
            originX: 'center',
            originY: 'center',
            selectable: false,
            evented: false
          });
          canvas.backgroundImage = img;
          canvas.renderAll();
        })
        .catch(err => console.error('Error loading background:', err));
    }
  }, [customization.background, currentStep]);

  // Add QR code to canvas when pending and canvas is ready
  useEffect(() => {
    if (pendingQrAdd && arQrCode && fabricCanvasRef.current && currentStep === 1 && !qrAddedToCanvas) {
      // Add QR code to the canvas
      FabricImage.fromURL(arQrCode)
        .then((img) => {
          const canvas = fabricCanvasRef.current;
          img.scale(0.5);
          img.set({
            left: canvas.width - 80,
            top: canvas.height - 80,
            originX: 'center',
            originY: 'center',
            selectable: true,
            hasControls: true,
            hasBorders: true,
            cornerColor: '#9333ea',
            cornerStyle: 'circle',
            borderColor: '#9333ea',
            borderScaleFactor: 2,
          });
          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.renderAll();
          setPendingQrAdd(false);
          setQrAddedToCanvas(true);
        })
        .catch(err => {
          console.error('Error adding QR to canvas:', err);
          setPendingQrAdd(false);
        });
    }
  }, [pendingQrAdd, arQrCode, currentStep, qrAddedToCanvas]);

  // Live text preview - update as user types
  useEffect(() => {
    if (!fabricCanvasRef.current) return;

    if (textInput.trim()) {
      if (liveTextObject) {
        // Update existing text
        liveTextObject.set('text', textInput);
        liveTextObject.set('fill', textColor);
        liveTextObject.set('fontSize', fontSize);
        liveTextObject.set('fontFamily', fontFamily);
        fabricCanvasRef.current.renderAll();
      } else {
        // Create new text object
        const text = new IText(textInput, {
          left: fabricCanvasRef.current.width / 2,
          top: fabricCanvasRef.current.height / 2,
          fontSize: fontSize,
          fill: textColor,
          fontFamily: fontFamily,
          originX: 'center',
          originY: 'center',
          editable: true,
        });

        fabricCanvasRef.current.add(text);
        fabricCanvasRef.current.setActiveObject(text);
        setLiveTextObject(text);
      }
    } else if (liveTextObject && !textInput.trim()) {
      // Remove text if input is empty
      fabricCanvasRef.current.remove(liveTextObject);
      setLiveTextObject(null);
      fabricCanvasRef.current.renderAll();
    }
  }, [textInput, textColor, fontSize, fontFamily]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      FabricImage.fromURL(event.target.result)
        .then((img) => {
          // Scale image to fit canvas
          const maxWidth = 200;
          const maxHeight = 200;
          const scale = Math.min(maxWidth / img.width, maxHeight / img.height);
          
          img.scale(scale);
          img.set({
            left: fabricCanvasRef.current.width / 2,
            top: fabricCanvasRef.current.height / 2,
            originX: 'center',
            originY: 'center',
          });

          // Apply shape clip path
          applyShapeToImage(img, selectedShape);
          
          // Apply frame
          applyFrameToImage(img, selectedFrame);

          fabricCanvasRef.current.add(img);
          fabricCanvasRef.current.setActiveObject(img);
          fabricCanvasRef.current.renderAll();
        });
    };
    reader.readAsDataURL(file);
  };

  const applyShapeToImage = (img, shape) => {
    const width = img.width * img.scaleX;
    const height = img.height * img.scaleY;

    switch (shape) {
      case 'circle':
        const radius = Math.min(width, height) / 2;
        img.clipPath = new fabric.Circle({
          radius: radius / img.scaleX,
          originX: 'center',
          originY: 'center',
        });
        break;
      case 'square':
        const size = Math.min(width, height);
        img.clipPath = new fabric.Rect({
          width: size / img.scaleX,
          height: size / img.scaleY,
          originX: 'center',
          originY: 'center',
        });
        break;
      case 'rounded':
        img.clipPath = new fabric.Rect({
          width: width / img.scaleX,
          height: height / img.scaleY,
          rx: 20 / img.scaleX,
          ry: 20 / img.scaleY,
          originX: 'center',
          originY: 'center',
        });
        break;
      case 'rectangle':
      default:
        img.clipPath = null;
        break;
    }
  };

  const applyFrameToImage = (img, frame) => {
    switch (frame) {
      case 'simple':
        img.set({
          stroke: '#000000',
          strokeWidth: 3
        });
        break;
      case 'double':
        img.set({
          stroke: '#000000',
          strokeWidth: 6,
          strokeDashArray: [10, 5]
        });
        break;
      case 'shadow':
        img.set({
          shadow: {
            color: 'rgba(0,0,0,0.5)',
            blur: 15,
            offsetX: 5,
            offsetY: 5
          }
        });
        break;
      case 'none':
      default:
        img.set({
          stroke: null,
          strokeWidth: 0,
          shadow: null
        });
        break;
    }
  };

  const deleteSelected = () => {
    if (!fabricCanvasRef.current) return;
    const activeObject = fabricCanvasRef.current.getActiveObject();
    if (activeObject) {
      if (activeObject === liveTextObject) {
        setLiveTextObject(null);
        setTextInput('');
      }
      fabricCanvasRef.current.remove(activeObject);
      fabricCanvasRef.current.renderAll();
    }
  };

  // Add clipart (PNG image) to canvas
  const addClipartToCanvas = (url, name) => {
    if (!fabricCanvasRef.current) return;
    
    setClipartLoading(true);
    const canvas = fabricCanvasRef.current;
    
    // Load image from URL
    FabricImage.fromURL(url, { crossOrigin: 'anonymous' })
      .then((img) => {
        // Scale image to reasonable size
        const maxSize = 100;
        const scale = Math.min(maxSize / img.width, maxSize / img.height);
        
        img.scale(scale);
        img.set({
          left: canvas.width / 2 + Math.random() * 100 - 50,
          top: canvas.height / 2 + Math.random() * 100 - 50,
          originX: 'center',
          originY: 'center',
          selectable: true,
          hasControls: true,
          hasBorders: true,
        });
        
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        setClipartLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load clipart:', err);
        setClipartLoading(false);
        // Fallback to emoji if image fails
        const fallbackEmoji = new IText('🎨', {
          left: canvas.width / 2,
          top: canvas.height / 2,
          fontSize: 60,
          originX: 'center',
          originY: 'center',
        });
        canvas.add(fallbackEmoji);
        canvas.renderAll();
      });
  };

  const addNewText = () => {
    if (!fabricCanvasRef.current) return;
    
    setLiveTextObject(null);
    setTextInput('Click to add new text');
    
    setTimeout(() => {
      setTextInput('');
    }, 100);
  };

  const handleNext = () => {
    // Save canvas data before moving to next step
    if (currentStep === 1 && fabricCanvasRef.current) {
      const canvasData = fabricCanvasRef.current.toJSON();
      const imageData = fabricCanvasRef.current.toDataURL({ format: 'png', quality: 1, multiplier: 3 });
      setCustomization({ 
        ...customization, 
        canvasData: canvasData,
        designImage: imageData
      });
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAddToCart = async () => {
    try {
      // Final rendered design: live canvas if still mounted, otherwise the snapshot saved when leaving the design step
      const canvasImage = fabricCanvasRef.current?.toDataURL({ format: 'png', quality: 1, multiplier: 3 }) || customization.designImage;
      if (!canvasImage || !canvasImage.startsWith('data:image')) {
        throw new Error('Your card design could not be captured. Please go back to the design step and try again.');
      }
      
      // Upload the design (base64 JSON) so the cart only holds a small DB-backed reference
      let cardImageUrl = canvasImage;
      try {
        const uploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload-temp-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image_data: canvasImage })
        });
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          cardImageUrl = uploadData.image_url || uploadData.url || canvasImage;
          console.log('[Wizard] Card image uploaded:', cardImageUrl);
        }
      } catch (uploadError) {
        console.error('[Wizard] Design upload failed, keeping base64 in cart:', uploadError);
      }

      // If AR is enabled and we have a greeting ID, upload the card as marker image
      if (arEnabled && arGreetingId) {
        try {
          // Create a clean version of the card for AR marker (the full card with QR)
          const markerBlob = await (await fetch(canvasImage)).blob();
          const markerFormData = new FormData();
          markerFormData.append('file', markerBlob, 'ar-marker.png');
          
          // Upload marker image
          const markerUploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ar/upload-marker`, {
            method: 'POST',
            body: markerFormData
          });
          
          if (markerUploadResponse.ok) {
            const markerData = await markerUploadResponse.json();
            
            // Update the AR greeting with the marker URL
            await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ar/greeting/${arGreetingId}/marker`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ marker_image_url: markerData.marker_url })
            });
            
            console.log('AR marker saved:', markerData.marker_url);
          }
        } catch (markerError) {
          console.error('Error saving AR marker:', markerError);
          // Don't block the add to cart if marker upload fails
        }
      }

      // Create text-postcard template with addresses
      const templateData = {
        name: `Custom Card - ${new Date().toLocaleString()}`,
        template_type: 'text-postcard',
        from_address: customization.from,
        to_address: customization.to,
        ingredients: `Edible greetings by potrait chocolate\n\nIngredients\nPotato starch, water, sunflower oil, edible colors. (water, propylene glycol, glycerin, and various FDA-certified food coloring dyes)`,
        nutrition_facts: `Nutrition Facts (per 7g serving)\nCalories: 0g, Carbohydrates: 0g, Fat: 0g, Protein: 0g, Sugars: 0g\n\npotraitchocolate@gmail.com\n55-230, hoea rd hawi, 96719-0687\nHawi, Hawaii\n8087562017`
      };

      // Create template
      const createResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(templateData)
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create template');
      }

      const template = await createResponse.json();
      const templateId = template._id || template.id;

      // Note: PDF downloads removed - envelope will be available for admin to print from orders page

      // Calculate total price including AR charge
      const arCharge = arEnabled ? 2.00 : 0;
      const totalPrice = parseFloat(product.price) + arCharge;

      // Add to cart with the design (include AR info if enabled)
      onAddToCart({
        ...product,
        price: totalPrice, // Updated price with AR charge
        customization: {
          ...customization,
          designImage: cardImageUrl, // Use uploaded URL, falls back to base64
          templateId: templateId,
          arEnabled: arEnabled,
          arGreetingId: arGreetingId,
          arQrCode: arQrCode,
          arCharge: arCharge // Track the AR charge separately
        }
      });

    } catch (error) {
      console.error('Error creating postcard:', error);
      alert(error?.message || 'Failed to create postcard. Please try again.');
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return customization.background !== null;
      case 1:
        // Design step - always valid (text and AR are optional)
        return true;
      case 2:
        // Envelope addresses - required
        return customization.from.trim().length > 0 && customization.to.trim().length > 0;
      case 3:
        return true;
      default:
        return false;
    }
  };

  // Generate AR URL for QR code
  const getARUrl = () => {
    const baseUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    const params = new URLSearchParams({
      message: arMessage,
      animation: arAnimation,
      reveal: arRevealMessage
    });
    if (arVideoUrl) {
      params.append('video', arVideoUrl);
    }
    return `${baseUrl}/ar/card?${params.toString()}`;
  };

  // Handle video upload for AR
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert('Video file is too large. Maximum size is 50MB.');
      return;
    }
    
    setArVideo(file);
    setVideoUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload video
      const uploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ar/upload-video`, {
        method: 'POST',
        body: formData
      });
      
      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();
        setArVideoUrl(uploadData.video_url);
        
        // Create AR greeting to get ID
        const greetingResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ar/create-greeting`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            video_url: uploadData.video_url,
            message: arMessage,
            animation: arAnimation,
            reveal_message: arRevealMessage
          })
        });
        
        if (greetingResponse.ok) {
          const greetingData = await greetingResponse.json();
          setArGreetingId(greetingData.greeting_id);
          
          // Generate QR code for the AR link
          const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
          const arUrl = `${frontendUrl}/ar/${greetingData.greeting_id}`;
          const qrDataUrl = await QRCode.toDataURL(arUrl, {
            width: 200,
            margin: 2,
            color: { dark: '#000000', light: '#FFFFFF' }
          });
          setArQrCode(qrDataUrl);
          
          // Set flag to add QR to canvas when we go back to editor step
          setPendingQrAdd(true);
          setQrAddedToCanvas(false);
          
          // Go back to editor step - QR will be added automatically via useEffect
          alert('✅ Video uploaded! QR code will be added to your card. Drag it to position where you want!');
          setCurrentStep(1); // Go back to editor step
        }
      } else {
        alert('Failed to upload video. Please try again.');
        setArVideo(null);
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Failed to upload video. Please try again.');
      setArVideo(null);
    } finally {
      setVideoUploading(false);
    }
  };

  // Add QR code to canvas (called when user wants to add it manually)
  const addQrCodeToCanvas = () => {
    if (!arQrCode || !fabricCanvasRef.current) {
      alert('Please upload a video first to generate QR code');
      return;
    }
    
    FabricImage.fromURL(arQrCode)
      .then((img) => {
        const canvas = fabricCanvasRef.current;
        img.scale(0.5);
        img.set({
          left: canvas.width - 80,
          top: canvas.height - 80,
          originX: 'center',
          originY: 'center',
          selectable: true,
          hasControls: true,
          hasBorders: true,
          cornerColor: '#9333ea',
          cornerStyle: 'circle',
          borderColor: '#9333ea',
          borderScaleFactor: 2,
        });
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        setQrAddedToCanvas(true);
      })
      .catch(err => {
        console.error('Error adding QR to canvas:', err);
      });
  };

  const removeVideo = () => {
    setArVideo(null);
    setArVideoUrl(null);
    setArQrCode(null);
    setArGreetingId(null);
    setQrAddedToCanvas(false);
    setPendingQrAdd(false);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-3xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 bg-clip-text text-transparent mb-2">
                Choose Your Design
              </h3>
              <p className="text-gray-500">Select a beautiful background for your greeting card</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {designs.map((image, index) => (
                <div
                  key={index}
                  onClick={() => {
                    setCustomization({ ...customization, background: image });
                  }}
                  className={`group relative cursor-pointer rounded-2xl overflow-hidden transition-all duration-500 transform hover:scale-105 hover:-rotate-1 ${
                    customization.background === image
                      ? 'ring-4 ring-purple-600 shadow-2xl shadow-purple-300/50 scale-105 rotate-1'
                      : 'ring-2 ring-gray-200 hover:ring-purple-400 hover:shadow-xl'
                  }`}
                  style={{ 
                    aspectRatio: '3/2',
                    animationDelay: `${index * 100}ms`
                  }}
                >
                  <img
                    src={image}
                    alt={`Design ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {/* Overlay gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent transition-opacity duration-300 ${
                    customization.background === image ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
                  }`} />
                  
                  {/* Selected checkmark with animation */}
                  {customization.background === image && (
                    <div className="absolute top-3 right-3 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full p-2 shadow-lg animate-bounce-in">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                  )}
                  
                  {/* Hover label */}
                  <div className={`absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent transform transition-all duration-300 ${
                    customization.background === image ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100'
                  }`}>
                    <p className="text-white text-sm font-medium">
                      {customization.background === image ? '✓ Selected' : 'Click to select'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Selection indicator */}
            {customization.background && (
              <div className="text-center animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full">
                  <Check className="w-4 h-4" />
                  <span className="font-medium">Design selected! Click Continue to customize</span>
                </div>
              </div>
            )}
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Design Your Card</h3>
            
            <div className="grid md:grid-cols-[1fr_320px] gap-6">
              {/* Canvas */}
              <div>
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-50 flex justify-center items-center p-4">
                  <CanvasErrorBoundary fallback={<div className="w-[600px] h-[400px] flex items-center justify-center text-gray-500">Loading canvas...</div>}>
                    <canvas key={canvasKey} ref={canvasRef} className="shadow-lg" />
                  </CanvasErrorBoundary>
                </div>
                <p className="text-sm text-gray-600 mt-2">💡 Drag to move • Double-click text to edit • Use corners to resize</p>
              </div>

              {/* Tools - Collapsible Accordion */}
              <div className="space-y-2">
                {/* Text Tools */}
                <Card className="overflow-hidden">
                  <button
                    onClick={() => setOpenSection(openSection === 'text' ? null : 'text')}
                    className="w-full p-3 bg-gradient-to-r from-purple-50 to-pink-50 flex items-center justify-between hover:from-purple-100 hover:to-pink-100 transition-all"
                  >
                    <span className="font-semibold flex items-center">
                      <Type className="w-4 h-4 mr-2" />
                      Text
                    </span>
                    <span className={`text-xl font-bold transition-transform ${openSection === 'text' ? 'rotate-45' : ''}`}>+</span>
                  </button>
                  {openSection === 'text' && (
                    <div className="p-3 space-y-3 border-t">
                      <textarea
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        placeholder="Type to see live preview..."
                        rows="2"
                        className="w-full px-3 py-2 border rounded-lg resize-none text-sm"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={fontFamily}
                          onChange={(e) => setFontFamily(e.target.value)}
                          className="px-2 py-1 border rounded text-sm"
                        >
                          {fonts.map(font => (
                            <option key={font} value={font}>{font}</option>
                          ))}
                        </select>
                        <input
                          type="range"
                          min="16"
                          max="72"
                          value={fontSize}
                          onChange={(e) => setFontSize(parseInt(e.target.value))}
                          className="w-full"
                          title={`Size: ${fontSize}px`}
                        />
                      </div>
                      <div className="grid grid-cols-8 gap-1">
                        {colors.map((color) => (
                          <button
                            key={color}
                            onClick={() => setTextColor(color)}
                            className={`w-6 h-6 rounded ${textColor === color ? 'ring-2 ring-purple-600' : ''}`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <Button onClick={addNewText} variant="outline" size="sm" className="w-full">
                        + Add Text
                      </Button>
                    </div>
                  )}
                </Card>

                {/* Photo Upload */}
                <Card className="overflow-hidden">
                  <button
                    onClick={() => setOpenSection(openSection === 'photo' ? null : 'photo')}
                    className="w-full p-3 bg-gradient-to-r from-blue-50 to-cyan-50 flex items-center justify-between hover:from-blue-100 hover:to-cyan-100 transition-all"
                  >
                    <span className="font-semibold flex items-center">
                      📷 Add Photo
                    </span>
                    <span className={`text-xl font-bold transition-transform ${openSection === 'photo' ? 'rotate-45' : ''}`}>+</span>
                  </button>
                  {openSection === 'photo' && (
                    <div className="p-3 space-y-2 border-t">
                      <div className="grid grid-cols-4 gap-1">
                        {shapes.map((shape) => (
                          <button
                            key={shape.id}
                            onClick={() => setSelectedShape(shape.id)}
                            className={`p-1 rounded text-lg ${selectedShape === shape.id ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
                            title={shape.name}
                          >
                            {shape.icon}
                          </button>
                        ))}
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <Button onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" className="w-full">
                        📤 Upload Photo
                      </Button>
                    </div>
                  )}
                </Card>

                {/* Clipart */}
                <Card className="overflow-hidden">
                  <button
                    onClick={() => setOpenSection(openSection === 'clipart' ? null : 'clipart')}
                    className="w-full p-3 bg-gradient-to-r from-orange-50 to-yellow-50 flex items-center justify-between hover:from-orange-100 hover:to-yellow-100 transition-all"
                  >
                    <span className="font-semibold flex items-center">
                      🎨 Free Clipart
                    </span>
                    <span className={`text-xl font-bold transition-transform ${openSection === 'clipart' ? 'rotate-45' : ''}`}>+</span>
                  </button>
                  {openSection === 'clipart' && (
                    <div className="p-3 space-y-2 border-t">
                      <div className="flex flex-wrap gap-1">
                        {clipartCategories.map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => setSelectedClipartCategory(cat.id)}
                            className={`px-2 py-1 rounded text-xs ${selectedClipartCategory === cat.id ? 'bg-orange-500 text-white' : 'bg-gray-100'}`}
                          >
                            {cat.icon} {cat.name}
                          </button>
                        ))}
                      </div>
                      <div className="grid grid-cols-4 gap-1 max-h-24 overflow-y-auto">
                        {clipartItems[selectedClipartCategory]?.map((item) => (
                          <button
                            key={item.id}
                            onClick={() => addClipartToCanvas(item.url, item.name)}
                            disabled={clipartLoading}
                            className="aspect-square bg-white rounded border hover:border-orange-400 p-0.5"
                            title={item.name}
                          >
                            <img src={item.url} alt={item.name} className="w-full h-full object-contain" loading="lazy" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>

                {/* AR Video */}
                <Card className="overflow-hidden border-2 border-purple-300">
                  <button
                    onClick={() => {
                      if (openSection !== 'ar') {
                        setOpenSection('ar');
                        setArEnabled(true); // Auto-enable when opening
                      } else {
                        setOpenSection(null);
                      }
                    }}
                    className="w-full p-3 bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-between hover:from-purple-200 hover:to-pink-200 transition-all"
                  >
                    <span className="font-semibold flex items-center">
                      🎬 AR Video <span className="ml-2 text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">+$2</span>
                    </span>
                    <div className="flex items-center gap-2">
                      {arEnabled && <span className="text-xs text-green-600 font-medium">ON</span>}
                      <span className={`text-xl font-bold transition-transform ${openSection === 'ar' ? 'rotate-45' : ''}`}>+</span>
                    </div>
                  </button>
                  {openSection === 'ar' && (
                    <div className="p-3 space-y-2 border-t">
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        onChange={handleVideoUpload}
                        className="hidden"
                      />
                      {!arVideo ? (
                        <button
                          onClick={() => videoInputRef.current?.click()}
                          disabled={videoUploading}
                          className="w-full p-3 border-2 border-dashed border-purple-300 rounded-lg hover:bg-purple-50 text-center"
                        >
                          <div className="text-2xl">📹</div>
                          <p className="text-sm text-purple-700">Upload Video</p>
                        </button>
                      ) : (
                        <div className="p-2 bg-green-100 rounded-lg flex items-center justify-between">
                          <span className="text-sm text-green-800 truncate">✅ {arVideo.name}</span>
                          <button onClick={removeVideo} className="text-red-600 ml-2">✕</button>
                        </div>
                      )}
                      {videoUploading && <p className="text-sm text-center text-purple-600 animate-pulse">Uploading...</p>}
                      {arQrCode && <p className="text-xs text-green-700 bg-green-50 p-2 rounded text-center">✅ QR code added!</p>}
                      <input
                        type="text"
                        value={arMessage}
                        onChange={(e) => setArMessage(e.target.value)}
                        placeholder="Greeting message..."
                        className="w-full px-2 py-1 text-sm border rounded"
                        maxLength={50}
                      />
                      <button
                        onClick={() => { setArEnabled(false); setOpenSection(null); removeVideo(); }}
                        className="w-full text-xs text-red-500 hover:text-red-700"
                      >
                        ✕ Remove AR Video
                      </button>
                    </div>
                  )}
                </Card>

                {/* Delete Button */}
                <Button
                  onClick={deleteSelected}
                  variant="outline"
                  size="sm"
                  className="w-full text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Selected
                </Button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Envelope Address</h3>
            <p className="text-gray-600 mb-4">These addresses will be printed on the envelope for mailing (not on the card)</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📤 From Address:
                </label>
                <textarea
                  value={customization.from}
                  onChange={(e) => setCustomization({ ...customization, from: e.target.value })}
                  placeholder="Your Name&#10;Street Address&#10;City, State ZIP"
                  rows="5"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-600 focus:outline-none transition-colors resize-none font-mono"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📥 To Address:
                </label>
                <textarea
                  value={customization.to}
                  onChange={(e) => setCustomization({ ...customization, to: e.target.value })}
                  placeholder="Recipient Name&#10;Street Address&#10;City, State ZIP"
                  rows="5"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-600 focus:outline-none transition-colors resize-none font-mono"
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                ✉️ These addresses are for the mailing envelope only. Your card design remains exactly as you created it.
              </p>
            </div>
          </div>
        );

      case 3:
        const arCharge = arEnabled ? 2.00 : 0;
        const totalPrice = (parseFloat(product.price) + arCharge).toFixed(2);
        
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Review Your Order</h3>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Card Design Preview:</h4>
                <div className="rounded-lg overflow-hidden shadow-xl border-4 border-gray-200">
                  {customization.designImage ? (
                    <img
                      src={customization.designImage}
                      alt="Your design"
                      className="w-full"
                    />
                  ) : (
                    <div className="aspect-[3/2] bg-gray-100 flex items-center justify-center">
                      <p className="text-gray-500">Loading preview...</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Order Summary</h4>
                  <p className="text-gray-700">{product.name}</p>
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Card Price:</span>
                      <span>${product.price}</span>
                    </div>
                    {arEnabled && (
                      <div className="flex justify-between text-purple-600">
                        <span>🎬 AR Video:</span>
                        <span>+$2.00</span>
                      </div>
                    )}
                    <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span className="text-purple-600">${totalPrice}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Envelope Addresses</h4>
                  <div className="text-sm space-y-2">
                    <div>
                      <span className="font-medium">From:</span>
                      <pre className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">{customization.from}</pre>
                    </div>
                    <div>
                      <span className="font-medium">To:</span>
                      <pre className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">{customization.to}</pre>
                    </div>
                  </div>
                </div>

                {arEnabled && (
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-purple-900 mb-2">🎬 AR Video Included</h4>
                    <p className="text-sm text-purple-700">QR code will be on your card for AR video playback!</p>
                  </div>
                )}

                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2">✓ Ready to Order</h4>
                  <ul className="space-y-1 text-sm text-green-800">
                    <li>✓ Design created</li>
                    <li>✓ Addresses provided</li>
                    {arEnabled && <li>✓ AR Video enabled</li>}
                    <li>✓ Ready for printing</li>
                  </ul>
                </div>

                <Button
                  onClick={handleAddToCart}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-6 text-lg font-bold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105"
                >
                  Add to Cart - ${totalPrice}
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const [slideDirection, setSlideDirection] = useState('right');
  const [isAnimating, setIsAnimating] = useState(false);

  // Save canvas state before step change
  const saveCanvasState = () => {
    if (fabricCanvasRef.current) {
      try {
        const designImage = fabricCanvasRef.current.toDataURL({ format: 'png', quality: 1, multiplier: 3 });
        setCustomization(prev => ({ ...prev, designImage }));
        
        // Dispose canvas properly before leaving step
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      } catch (e) {
        console.log('Canvas save error:', e);
      }
    }
  };

  const handleNextWithAnimation = () => {
    if (currentStep < steps.length - 1 && isStepValid()) {
      // Save canvas state when leaving step 1
      if (currentStep === 1) {
        saveCanvasState();
      }
      
      setSlideDirection('right');
      setIsAnimating(true);
      setTimeout(() => {
        if (currentStep === 1) {
          setCanvasKey(prev => prev + 1); // Force canvas re-create on return
        }
        setCurrentStep(currentStep + 1);
        setTimeout(() => setIsAnimating(false), 50);
      }, 300);
    }
  };

  const handleBackWithAnimation = () => {
    if (currentStep > 0) {
      setSlideDirection('left');
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setTimeout(() => setIsAnimating(false), 50);
      }, 300);
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Animated Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 -z-10 animate-gradient-shift" />
      
      {/* Progress Bar - Oreo Style */}
      <div className="mb-10 relative">
        {/* Background track */}
        <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 rounded-full mx-16" />
        
        {/* Animated progress fill */}
        <div 
          className="absolute top-5 left-0 h-1 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 rounded-full mx-16 transition-all duration-700 ease-out"
          style={{ width: `${(currentStep / (steps.length - 1)) * (100 - 15)}%` }}
        />
        
        <div className="flex items-center justify-between relative z-10">
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center flex-1">
              {/* Step Circle with Animation */}
              <div
                className={`relative w-10 h-10 md:w-14 md:h-14 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-500 transform cursor-pointer
                  ${index < currentStep
                    ? 'bg-gradient-to-br from-green-400 to-green-600 text-white shadow-lg shadow-green-300/50'
                    : index === currentStep
                    ? 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500 text-white shadow-xl shadow-purple-400/50 scale-110'
                    : 'bg-white border-2 border-gray-300 text-gray-400'
                  }
                  ${index <= currentStep ? 'hover:scale-125' : ''}
                `}
                onClick={() => index < currentStep && setCurrentStep(index)}
              >
                {/* Pulse animation for current step */}
                {index === currentStep && (
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 animate-ping opacity-30" />
                )}
                
                {/* Checkmark or number */}
                <span className="relative z-10">
                  {index < currentStep ? (
                    <Check className="w-5 h-5 md:w-7 md:h-7 animate-bounce-once" />
                  ) : (
                    <span className={index === currentStep ? 'animate-pulse' : ''}>{index + 1}</span>
                  )}
                </span>
                
                {/* Sparkle effect for completed steps */}
                {index < currentStep && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full animate-sparkle flex items-center justify-center">
                    <span className="text-xs">✓</span>
                  </div>
                )}
              </div>
              
              {/* Step Label */}
              <div className="mt-3 text-center hidden md:block">
                <div className={`text-sm font-semibold transition-all duration-300 ${
                  index === currentStep 
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 scale-110' 
                    : index < currentStep 
                    ? 'text-green-600' 
                    : 'text-gray-500'
                }`}>
                  {step.title}
                </div>
                <div className={`text-xs mt-1 transition-all duration-300 ${
                  index === currentStep ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {step.description}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Step Content with Slide Animation */}
      <Card className="p-6 md:p-8 min-h-[500px] shadow-2xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden relative">
        {/* Decorative corner elements */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-purple-200/30 to-transparent rounded-bl-full" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-200/30 to-transparent rounded-tr-full" />
        
        <div 
          className={`relative z-10 transition-all duration-500 ease-out transform
            ${isAnimating 
              ? slideDirection === 'right' 
                ? 'opacity-0 -translate-x-10' 
                : 'opacity-0 translate-x-10'
              : 'opacity-100 translate-x-0'
            }
          `}
        >
          {renderStepContent()}
        </div>
      </Card>

      {/* Navigation Buttons - Oreo Style */}
      <div className="flex justify-between mt-8 gap-4">
        <Button
          onClick={handleBackWithAnimation}
          disabled={currentStep === 0 || isAnimating}
          variant="outline"
          className="group px-6 py-6 text-lg disabled:opacity-30 transition-all duration-300 hover:scale-105 hover:shadow-lg border-2 hover:border-purple-400 hover:bg-purple-50"
        >
          <ChevronLeft className="w-5 h-5 mr-2 transition-transform group-hover:-translate-x-1" />
          Back
        </Button>

        {currentStep < steps.length - 1 && (
          <Button
            onClick={handleNextWithAnimation}
            disabled={!isStepValid() || isAnimating}
            className="group px-8 py-6 text-lg bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white disabled:opacity-30 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-purple-400/30 relative overflow-hidden"
          >
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
            <span className="relative z-10 flex items-center">
              Continue
              <ChevronRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
            </span>
          </Button>
        )}
      </div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes gradient-shift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient-shift {
          background-size: 200% 200%;
          animation: gradient-shift 15s ease infinite;
        }
        @keyframes bounce-once {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-bounce-once {
          animation: bounce-once 0.5s ease-out;
        }
        @keyframes sparkle {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.8; }
        }
        .animate-sparkle {
          animation: sparkle 2s ease-in-out infinite;
        }
        @keyframes bounce-in {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-bounce-in {
          animation: bounce-in 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default CustomDesignerWizard;
